// iter_ONLINE_CUSTOMER_BOOKINGS (2026-02-28) — Public booking manage +
// cancel page. Reached from the confirmation/reminder email link.
// Route: /book/manage/:token. Unauthenticated.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  const ampm = h < 12 ? "am" : "pm";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")}${ampm}`;
}

export default function PublicBookingManage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | not_found | cancelled | error
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/bookings/manage/${token}`);
        setData(r.data);
        setState(r.data.booking?.status === "cancelled" ? "cancelled" : "ready");
      } catch {
        setState("not_found");
      }
    })();
  }, [token]);

  const cancel = async () => {
    if (!window.confirm("Cancel this booking? This cannot be undone.")) return;
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/public/bookings/manage/${token}/cancel`,
                                    { confirm: true });
      setData({ ...data, booking: r.data.booking });
      setState("cancelled");
      toast.success("Booking cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed.");
    } finally { setConfirming(false); }
  };

  if (state === "loading") return <div className="p-6 text-center text-stone-500">Loading…</div>;
  if (state === "not_found") return (
    <div className="p-6 text-center text-red-600" data-testid="pbm-not-found">
      Booking not found or link expired.
    </div>
  );

  const b = data.booking;
  const v = data.venue;
  return (
    <div className="min-h-screen bg-stone-50 p-4 sm:p-8" data-testid="pbm-page">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-lg font-semibold">Your booking</h1>
        <dl className="mt-3 text-sm space-y-1">
          <Row k="Venue" v={v?.name} />
          <Row k="Reference" v={b.booking_reference} testid="pbm-ref" />
          <Row k="Name" v={b.customer_name} />
          <Row k="Date" v={b.booking_date} />
          <Row k="Time" v={fmtTime12(b.booking_time)} />
          <Row k="Diners" v={b.party_size} />
          <Row k="Status" v={b.status} testid="pbm-status" />
        </dl>
        {state === "cancelled" ? (
          <div className="mt-4 text-sm text-stone-600" data-testid="pbm-cancelled">
            This booking has been cancelled.
          </div>
        ) : (
          <button data-testid="pbm-cancel-btn"
                  disabled={confirming || !data.can_cancel}
                  onClick={cancel}
                  className={`w-full mt-4 h-11 rounded-md font-semibold ${
                    data.can_cancel
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : "bg-stone-200 text-stone-500 cursor-not-allowed"}`}>
            {confirming ? "Cancelling…" : "Cancel booking"}
          </button>
        )}
        <p className="text-xs text-stone-500 mt-4">
          To make changes other than cancelling, please contact the venue directly
          {v?.phone && ` on ${v.phone}`}.
        </p>
      </div>
    </div>
  );
}

function Row({ k, v, testid }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-stone-500">{k}</dt>
      <dd className="font-medium text-right" data-testid={testid}>{v}</dd>
    </div>
  );
}
